/**
 * Calculator-style expression reducer.
 *
 * Operator precedence is intentionally **left-to-right** to match a physical
 * calculator UX (e.g. `2 × 3 + 4` evaluates as `(2 × 3) + 4 = 10`, not `14`).
 * The reducer is pure: it never mutates inputs.
 *
 * `amountMinor` is the canonical money value in minor units (cents),
 * computed via `Math.round(value * 100)` with EPSILON-aware rounding to
 * avoid IEEE-754 drift on multi-step decimal inputs (e.g. 0.1 + 0.2).
 *
 * Division by zero is **clamped to 0** with the `divisionByZero: true` flag
 * surfaced on the resulting state, so the UI can show a friendly message.
 */

export type CalcOp = '+' | '-' | '×' | '÷';

export type CalcInput =
  | { type: 'digit'; d: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 }
  | { type: 'dot' }
  | { type: 'op'; op: CalcOp }
  | { type: 'equals' }
  | { type: 'clear' }
  | { type: 'backspace' };

export interface CalcState {
  /** Current operand being typed, e.g. "30". Always non-empty (never ""). */
  display: string;
  /** Running expression, e.g. "70 + 30 + " (trailing space when op pending). */
  expression: string;
  /** Final value in minor units; refreshed on every reduce step. */
  amountMinor: number;
  /** Whether the most recent `=` triggered a divide-by-zero. */
  divisionByZero: boolean;
}

interface InternalState extends CalcState {
  /** Accumulator for left-to-right evaluation; null when nothing pending. */
  accumulator: number | null;
  /** Pending operator awaiting its right operand; null when none pending. */
  pendingOp: CalcOp | null;
  /** True when display shows the accumulator (not yet typed) — next digit replaces it. */
  awaitingOperand: boolean;
  /** True after `=` finalised the expression; next digit starts fresh. */
  finalised: boolean;
}

const INITIAL_INTERNAL: InternalState = {
  display: '0',
  expression: '',
  amountMinor: 0,
  divisionByZero: false,
  accumulator: null,
  pendingOp: null,
  awaitingOperand: false,
  finalised: false,
};

export function calcInitial(): CalcState {
  return publicView(INITIAL_INTERNAL);
}

export function calcReduce(state: CalcState, input: CalcInput): CalcState {
  const internal = toInternal(state);
  const next = step(internal, input);
  return publicView(next);
}

// ──────────────────────────────────────────────────────────────────────────
// Internal helpers
// ──────────────────────────────────────────────────────────────────────────

function step(s: InternalState, input: CalcInput): InternalState {
  switch (input.type) {
    case 'clear':
      return { ...INITIAL_INTERNAL };
    case 'digit':
      return applyDigit(s, input.d);
    case 'dot':
      return applyDot(s);
    case 'backspace':
      return applyBackspace(s);
    case 'op':
      return applyOp(s, input.op);
    case 'equals':
      return applyEquals(s);
  }
}

function applyDigit(s: InternalState, d: number): InternalState {
  // After `=`, a new digit starts a fresh expression.
  if (s.finalised) {
    return {
      ...INITIAL_INTERNAL,
      display: String(d),
      amountMinor: toMinor(d),
    };
  }
  // Awaiting a fresh operand (display is showing accumulator-as-placeholder "0").
  if (s.awaitingOperand) {
    return {
      ...s,
      display: String(d),
      amountMinor: toMinor(d),
      awaitingOperand: false,
    };
  }
  const nextDisplay = s.display === '0' ? String(d) : s.display + String(d);
  const value = parseFloat(nextDisplay);
  return {
    ...s,
    display: nextDisplay,
    amountMinor: toMinor(value),
  };
}

function applyDot(s: InternalState): InternalState {
  if (s.finalised) {
    return {
      ...INITIAL_INTERNAL,
      display: '0.',
    };
  }
  if (s.awaitingOperand) {
    return {
      ...s,
      display: '0.',
      amountMinor: 0,
      awaitingOperand: false,
    };
  }
  if (s.display.includes('.')) return s;
  return { ...s, display: s.display + '.' };
}

function applyBackspace(s: InternalState): InternalState {
  if (s.finalised) return s;
  if (s.awaitingOperand) return s;
  if (s.display.length <= 1) {
    if (s.display === '0') return s;
    return { ...s, display: '0', amountMinor: 0 };
  }
  const next = s.display.slice(0, -1);
  const safe = next === '' || next === '-' ? '0' : next;
  const value = parseFloat(safe);
  return {
    ...s,
    display: safe,
    amountMinor: Number.isFinite(value) ? toMinor(value) : 0,
  };
}

function applyOp(s: InternalState, op: CalcOp): InternalState {
  // After equals, chain: use the finalised display as the new left operand.
  if (s.finalised) {
    const leftValue = parseFloat(s.display);
    return {
      ...INITIAL_INTERNAL,
      display: '0',
      accumulator: leftValue,
      pendingOp: op,
      expression: `${formatNumber(leftValue)} ${op} `,
      awaitingOperand: true,
      amountMinor: toMinor(leftValue),
    };
  }

  // Replacing the pending op (user pressed `+` then `-` without typing a digit).
  if (s.awaitingOperand && s.pendingOp !== null && s.accumulator !== null) {
    return {
      ...s,
      pendingOp: op,
      expression: `${formatNumber(s.accumulator)} ${op} `,
    };
  }

  const rightValue = parseFloat(s.display);

  // No pending op yet → seed accumulator with current display.
  if (s.pendingOp === null || s.accumulator === null) {
    return {
      ...s,
      accumulator: rightValue,
      pendingOp: op,
      display: '0',
      expression: `${formatNumber(rightValue)} ${op} `,
      awaitingOperand: true,
      amountMinor: toMinor(rightValue),
    };
  }

  // Reduce the pending pair (accumulator op rightValue) before chaining.
  const reduced = compute(s.accumulator, s.pendingOp, rightValue);
  if (reduced.divisionByZero) {
    return { ...INITIAL_INTERNAL, divisionByZero: true, finalised: true };
  }
  return {
    ...s,
    accumulator: reduced.value,
    pendingOp: op,
    display: '0',
    amountMinor: toMinor(reduced.value),
    expression: `${formatNumber(reduced.value)} ${op} `,
    awaitingOperand: true,
  };
}

function applyEquals(s: InternalState): InternalState {
  // No pending op → finalise current display (idempotent on repeated `=`).
  if (s.pendingOp === null || s.accumulator === null) {
    const value = parseFloat(s.display);
    return {
      ...s,
      finalised: true,
      accumulator: null,
      pendingOp: null,
      amountMinor: toMinor(value),
      display: formatNumber(value),
      expression: '',
      divisionByZero: false,
      awaitingOperand: false,
    };
  }

  // If user pressed `op` then `=` directly, treat right operand as left
  // (mirrors most physical calculators: `5 + =` → 10. We choose `5` per spec).
  // Spec example: "5 +" then "=" → 5. So the right operand is 0 when none typed,
  // unless the operator is + or -, in which case the result equals the accumulator
  // when right is 0… but actually `5 + 0 = 5` works. For `×` and `÷` this would
  // give 0, which is surprising. So we treat "no second operand" as "skip the op".
  if (s.awaitingOperand) {
    return {
      ...s,
      finalised: true,
      accumulator: null,
      pendingOp: null,
      display: formatNumber(s.accumulator),
      amountMinor: toMinor(s.accumulator),
      expression: '',
      divisionByZero: false,
      awaitingOperand: false,
    };
  }

  const rightValue = parseFloat(s.display);
  const reduced = compute(s.accumulator, s.pendingOp, rightValue);
  if (reduced.divisionByZero) {
    return {
      ...INITIAL_INTERNAL,
      divisionByZero: true,
      finalised: true,
    };
  }
  return {
    ...s,
    finalised: true,
    accumulator: null,
    pendingOp: null,
    display: formatNumber(reduced.value),
    amountMinor: toMinor(reduced.value),
    expression: '',
    awaitingOperand: false,
    divisionByZero: false,
  };
}

interface ComputeResult {
  value: number;
  divisionByZero: boolean;
}

function compute(left: number, op: CalcOp, right: number): ComputeResult {
  switch (op) {
    case '+':
      return { value: roundDrift(left + right), divisionByZero: false };
    case '-':
      return { value: roundDrift(left - right), divisionByZero: false };
    case '×':
      return { value: roundDrift(left * right), divisionByZero: false };
    case '÷':
      if (right === 0) return { value: 0, divisionByZero: true };
      return { value: roundDrift(left / right), divisionByZero: false };
  }
}

/**
 * Round away IEEE-754 binary drift while preserving up to ~10 decimal digits
 * of precision. Multiplying by 1e10, rounding, then dividing collapses noise
 * like `0.30000000000000004` back to `0.3`.
 */
function roundDrift(n: number): number {
  if (!Number.isFinite(n)) return n;
  const scaled = n * 1e10;
  const nudged =
    scaled + Math.sign(scaled) * Number.EPSILON * Math.abs(scaled);
  return Math.round(nudged) / 1e10;
}

function toMinor(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const cents = roundDrift(value) * 100;
  return Math.round(cents);
}

/** Compact number → string for display & expression text. Strips trailing zeros. */
function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const fixed = n.toFixed(10);
  const trimmed = fixed.replace(/\.?0+$/, '');
  return trimmed === '' || trimmed === '-' ? '0' : trimmed;
}

/**
 * Hidden bookkeeping is attached to the returned CalcState under a Symbol
 * key. This keeps the declared public type minimal while preserving purity
 * — every reduce step produces a fresh object with the new bookkeeping.
 */
const HIDDEN = Symbol.for('mochia.calc.hidden');

interface Hidden {
  accumulator: number | null;
  pendingOp: CalcOp | null;
  awaitingOperand: boolean;
  finalised: boolean;
}

type StateWithHidden = CalcState & { [HIDDEN]?: Hidden };

function publicView(s: InternalState): CalcState {
  const out: StateWithHidden = {
    display: s.display,
    expression: s.expression,
    amountMinor: s.amountMinor,
    divisionByZero: s.divisionByZero,
  };
  out[HIDDEN] = {
    accumulator: s.accumulator,
    pendingOp: s.pendingOp,
    awaitingOperand: s.awaitingOperand,
    finalised: s.finalised,
  };
  return out;
}

/**
 * Reconstructs an InternalState from a public CalcState. Prefers the hidden
 * bookkeeping attached by `publicView`; falls back to parsing the running
 * expression so externally-constructed states (e.g. `calcInitial()` or test
 * fixtures) still work.
 */
function toInternal(s: CalcState): InternalState {
  const hidden = (s as StateWithHidden)[HIDDEN];
  if (hidden) {
    return {
      display: s.display,
      expression: s.expression,
      amountMinor: s.amountMinor,
      divisionByZero: s.divisionByZero,
      accumulator: hidden.accumulator,
      pendingOp: hidden.pendingOp,
      awaitingOperand: hidden.awaitingOperand,
      finalised: hidden.finalised,
    };
  }
  let accumulator: number | null = null;
  let pendingOp: CalcOp | null = null;
  let awaitingOperand = false;
  if (s.expression.length > 0) {
    const m = /^(-?\d+(?:\.\d+)?)\s+([+\-×÷])\s+$/.exec(s.expression);
    if (m && m[1] !== undefined && m[2] !== undefined) {
      accumulator = parseFloat(m[1]);
      pendingOp = m[2] as CalcOp;
      awaitingOperand = s.display === '0';
    }
  }
  return {
    display: s.display,
    expression: s.expression,
    amountMinor: s.amountMinor,
    divisionByZero: s.divisionByZero,
    accumulator,
    pendingOp,
    awaitingOperand,
    finalised: false,
  };
}
